import React from 'react';
import styled from 'styled-components';

import TripAdvisorLogo from './TripAdvisorLogo';
import GoogleLogo from './GoogleLogo';
import FacebookLogo from './FacebookLogo';
import YelpLogo from './YelpLogo';
import { getUrl } from '@/utils/review-links';
import { media } from '@/styles/theme';
import type { ReviewLinkProps } from '@/types';

const Container = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  padding: 14px;
  color: #2d96b3;

  &:hover {
    text-decoration: underline;
  }

  ${media.tablet} {
    padding: 18px;
  }
`;

const CustomButton = styled.button<{ $primaryColor: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ $primaryColor }) => $primaryColor}CC; /* 80% opacity */
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.2s ease;
  font-family: inherit;
  font-size: inherit;
  padding: 8px 12px;

  &:hover {
    background-color: ${({ $primaryColor }) =>
      $primaryColor}B3; /* 70% opacity on hover */
  }

  &:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
  }

  ${media.tablet} {
    padding: 12px 16px;
  }
`;

const Label = styled.span`
  margin-top: 8px;
  font-size: 14px;
`;

const CustomButtonLabel = styled.span`
  font-size: 14px;
`;

const getProviderLogo = (providerSlug: string) => {
  switch (providerSlug) {
    case 'tripadvisor':
      return <TripAdvisorLogo />;
    case 'google':
      return <GoogleLogo />;
    case 'facebook':
      return <FacebookLogo />;
    case 'yelp':
      return <YelpLogo />;
    case 'custom':
      return null; // Custom providers don't have logos
    default:
      return null;
  }
};

const ReviewLink: React.FC<ReviewLinkProps> = ({
  id,
  provider,
  place_id,
  page_id,
  profile_url,
  cid,
  fid,
  review_submit_url,
  name,
  primaryColor,
  addReviewLinkClick,
}) => {
  // Use review_submit_url if provided, otherwise generate URL from provider data
  const reviewUrl =
    review_submit_url ||
    getUrl(provider.slug, {
      id,
      enabled: true,
      provider,
      place_id,
      page_id,
      profile_url,
      cid,
      fid,
      name,
    });

  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => {
    event.preventDefault();
    addReviewLinkClick(id, provider.slug);

    // Always open in new tab
    window.open(reviewUrl, '_blank', 'noopener,noreferrer');
  };

  // For custom providers, use the name field; otherwise use provider.provider
  const displayName =
    provider.slug === 'custom'
      ? name
      : provider.provider ||
        provider.slug.charAt(0).toUpperCase() + provider.slug.slice(1); // Fallback to capitalized slug

  // For custom providers, render a button; for regular providers, render a link
  if (provider.slug === 'custom') {
    return (
      <CustomButton
        onClick={handleClick}
        type="button"
        $primaryColor={primaryColor}
      >
        <CustomButtonLabel>{displayName}</CustomButtonLabel>
      </CustomButton>
    );
  }

  return (
    <Container
      href={reviewUrl}
      onClick={handleClick}
      role="button"
      tabIndex={0}
    >
      {getProviderLogo(provider.slug)}
      <Label>{displayName}</Label>
    </Container>
  );
};

export default ReviewLink;
